import { useState, useEffect } from "react";
import SortableTableHead, { SortConfig, nextSort, sortData } from "./SortableTableHead";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, Users, UserCheck, ChevronLeft, ChevronRight, ShoppingCart, Tag, X, Download, CalendarIcon, CheckSquare, ShieldAlert, FileText, CheckCircle2, XCircle, Eye, Clock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { getGlp1Flags } from "@/lib/glp1-screening";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import PatientDetailDrawer from "./PatientDetailDrawer";

interface Patient {
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  assigned_provider_id: string | null;
  last_checkup_date: string | null;
  last_sign_in_at: string | null;
  created_at: string;
  email?: string;
  order_count?: number;
  active_order_count?: number;
  tags?: string[];
  glp1Flags?: string[];
}

interface Provider {
  user_id: string;
  first_name: string | null;
  last_name: string | null;
}

interface PatientManagementProps {
  onNavigateToOrders?: (patientId: string) => void;
  highlightPatientId?: string | null;
}

const NONE_VALUE = "__none__";


const TAG_COLORS: Record<string, string> = {
  "VIP": "bg-amber-100 text-amber-800",
  "Follow-up needed": "bg-orange-100 text-orange-800",
  "New patient": "bg-blue-100 text-blue-800",
  "High priority": "bg-red-100 text-red-800",
  "Stable": "bg-emerald-100 text-emerald-800",
  "On hold": "bg-slate-100 text-slate-700",
  "Flagged": "bg-rose-100 text-rose-800",
};
const DEFAULT_TAG_COLOR = "bg-secondary text-secondary-foreground";

const getTagColor = (tag: string): string => TAG_COLORS[tag] || DEFAULT_TAG_COLOR;

const PatientManagement = ({ onNavigateToOrders, highlightPatientId }: PatientManagementProps) => {
  const { toast } = useToast();
  const [patients, setPatients] = useState<Patient[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [search, setSearch] = useState("");
  const [tagFilter, setTagFilter] = useState<string | null>(null);
  const [glp1Only, setGlp1Only] = useState(false);
  const [allTags, setAllTags] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState<Date | undefined>(undefined);
  const [dateTo, setDateTo] = useState<Date | undefined>(undefined);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkProviderId, setBulkProviderId] = useState<string | null>(null);
  const [bulkAssigning, setBulkAssigning] = useState(false);
  const [assessmentStatusMap, setAssessmentStatusMap] = useState<Record<string, string>>({});
  const [assessmentFilter, setAssessmentFilter] = useState<string | null>(null);
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [patientsWithAppointments, setPatientsWithAppointments] = useState<Set<string>>(new Set());
  const [rejectingPatientId, setRejectingPatientId] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [viewingAssessmentPatientId, setViewingAssessmentPatientId] = useState<string | null>(null);
  const [assessmentDataMap, setAssessmentDataMap] = useState<Record<string, { status: string; goal: string | null; conditions: string | null; allergies: string | null; medications: string | null; peptide_interest: string | null; additional_info: string | null; created_at: string; updated_at?: string }>>({});
  const pageSize = 25;
  const [patientSort, setPatientSort] = useState<SortConfig>({ key: "created_at", direction: "desc" });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);

    // Fetch all user roles to identify patients (users without provider/admin roles, or all users)
    const [profilesRes, rolesRes, emailsRes, ordersRes, notesRes, assessmentsRes, appointmentsRes] = await Promise.all([
      supabase.from("profiles").select("user_id, first_name, last_name, phone, assigned_provider_id, last_checkup_date, last_sign_in_at, created_at"),
      supabase.from("user_roles").select("user_id, role"),
      supabase.rpc("get_user_emails"),
      supabase.from("orders").select("patient_id, status"),
      supabase.from("patient_notes").select("patient_id, tags"),
      supabase.from("health_assessments").select("user_id, status, goal, conditions, allergies, medications, peptide_interest, additional_info, created_at, updated_at"),
      supabase.from("appointments").select("patient_id, status"),
    ]);

    // Build set of patients who have any appointment
    const apptSet = new Set<string>();
    ((appointmentsRes.data as any[]) || []).forEach((a: { patient_id: string }) => {
      apptSet.add(a.patient_id);
    });
    setPatientsWithAppointments(apptSet);
    const assessStatusMap: Record<string, string> = {};
    const assessDataMap: Record<string, any> = {};
    ((assessmentsRes.data as any[]) || []).forEach((a: any) => {
      const priority: Record<string, number> = { approved: 3, reviewed: 2, pending: 1, rejected: 0 };
      const existing = assessStatusMap[a.user_id];
      if (!existing || (priority[a.status] ?? 0) > (priority[existing] ?? 0)) {
        assessStatusMap[a.user_id] = a.status;
        assessDataMap[a.user_id] = a;
      }
    });
    setAssessmentStatusMap(assessStatusMap);
    setAssessmentDataMap(assessDataMap);

    const emailMap: Record<string, string> = {};
    ((emailsRes.data as any[]) || []).forEach((e: { user_id: string; email: string }) => {
      emailMap[e.user_id] = e.email;
    });

    const orderCountMap: Record<string, number> = {};
    const activeOrderCountMap: Record<string, number> = {};
    ((ordersRes.data as any[]) || []).forEach((o: { patient_id: string | null; status: string }) => {
      if (o.patient_id) {
        orderCountMap[o.patient_id] = (orderCountMap[o.patient_id] || 0) + 1;
        if (o.status === "active" || o.status === "pending") {
          activeOrderCountMap[o.patient_id] = (activeOrderCountMap[o.patient_id] || 0) + 1;
        }
      }
    });

    // Build per-patient tag sets and collect all unique tags
    const patientTagMap: Record<string, Set<string>> = {};
    const tagSet = new Set<string>();
    ((notesRes.data as any[]) || []).forEach((n: { patient_id: string; tags: string[] | null }) => {
      if (n.tags && n.tags.length > 0) {
        if (!patientTagMap[n.patient_id]) patientTagMap[n.patient_id] = new Set();
        n.tags.forEach((t) => { patientTagMap[n.patient_id].add(t); tagSet.add(t); });
      }
    });
    setAllTags(Array.from(tagSet).sort());

    // Build per-patient GLP-1 flag map from assessments
    const glp1FlagMap: Record<string, string[]> = {};
    ((assessmentsRes.data as any[]) || []).forEach((a: { user_id: string; conditions: string | null; additional_info: string | null }) => {
      const flags = getGlp1Flags(a.conditions, a.additional_info);
      if (flags.length > 0) {
        if (!glp1FlagMap[a.user_id]) glp1FlagMap[a.user_id] = [];
        flags.forEach(f => { if (!glp1FlagMap[a.user_id].includes(f)) glp1FlagMap[a.user_id].push(f); });
      }
    });

    const roleMap: Record<string, string[]> = {};
    (rolesRes.data || []).forEach((r) => {
      if (!roleMap[r.user_id]) roleMap[r.user_id] = [];
      roleMap[r.user_id].push(r.role);
    });

    // All profiles are patients (they may also have other roles)
    const allPatients: Patient[] = (profilesRes.data || []).map((p) => ({
      ...p,
      email: emailMap[p.user_id] || undefined,
      order_count: orderCountMap[p.user_id] || 0,
      active_order_count: activeOrderCountMap[p.user_id] || 0,
      tags: patientTagMap[p.user_id] ? Array.from(patientTagMap[p.user_id]) : [],
      glp1Flags: glp1FlagMap[p.user_id] || [],
    }));
    setPatients(allPatients);

    // Providers are users with the provider role
    const providerIds = Object.entries(roleMap)
      .filter(([, roles]) => roles.includes("provider"))
      .map(([id]) => id);

    if (providerIds.length > 0) {
      const { data: provProfiles } = await supabase
        .from("profiles")
        .select("user_id, first_name, last_name")
        .in("user_id", providerIds);
      setProviders(provProfiles || []);
    }

    setLoading(false);
  };

  const assignProvider = async (patientUserId: string, providerId: string | null) => {
    const { error } = await supabase
      .from("profiles")
      .update({ assigned_provider_id: providerId })
      .eq("user_id", patientUserId);

    if (error) {
      toast({ title: "Error", description: "Could not update provider assignment.", variant: "destructive" });
      return;
    }

    setPatients((prev) =>
      prev.map((p) => (p.user_id === patientUserId ? { ...p, assigned_provider_id: providerId } : p))
    );

    const provName = providerId
      ? providers.find((p) => p.user_id === providerId)
        ? `Dr. ${providers.find((p) => p.user_id === providerId)?.last_name || "Unknown"}`
        : "Provider"
      : "None";
    toast({ title: "Provider updated", description: `Assigned to ${provName}` });
  };

  const approveAssessment = async (patientUserId: string) => {
    const { error } = await supabase
      .from("health_assessments")
      .update({ status: "approved" })
      .eq("user_id", patientUserId)
      .eq("status", "pending");

    if (error) {
      toast({ title: "Error", description: "Failed to approve assessment.", variant: "destructive" });
    } else {
      setAssessmentStatusMap((prev) => ({ ...prev, [patientUserId]: "approved" }));
      setAssessmentDataMap((prev) => ({ ...prev, [patientUserId]: { ...prev[patientUserId], status: "approved" } }));
      toast({ title: "Assessment approved", description: "Provider assignment is now available." });
    }
  };

  const rejectAssessment = async () => {
    if (!rejectingPatientId) return;
    const reason = rejectionReason.trim() || "Rejected by admin";
    const { error } = await supabase
      .from("health_assessments")
      .update({ status: "rejected", additional_info: reason })
      .eq("user_id", rejectingPatientId)
      .eq("status", "pending");

    if (error) {
      toast({ title: "Error", description: "Failed to reject assessment.", variant: "destructive" });
    } else {
      setAssessmentStatusMap((prev) => ({ ...prev, [rejectingPatientId]: "rejected" }));
      setAssessmentDataMap((prev) => ({ ...prev, [rejectingPatientId]: { ...prev[rejectingPatientId], status: "rejected", additional_info: reason } }));
      toast({ title: "Assessment rejected", description: reason });
    }
    setRejectingPatientId(null);
    setRejectionReason("");
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const pageIds = paginatedPatients.map((p) => p.user_id);
    const allSelected = pageIds.every((id) => selectedIds.has(id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      pageIds.forEach((id) => allSelected ? next.delete(id) : next.add(id));
      return next;
    });
  };

  const bulkAssignProvider = async () => {
    if (selectedIds.size === 0) return;
    setBulkAssigning(true);
    const providerId = bulkProviderId === NONE_VALUE ? null : bulkProviderId;
    // Only assign patients who have completed an assessment
    const ids = Array.from(selectedIds).filter((id) => !!assessmentStatusMap[id] && assessmentStatusMap[id] !== "pending");
    const skipped = selectedIds.size - ids.length;

    if (ids.length === 0) {
      toast({ title: "No eligible patients", description: "All selected patients are missing assessments.", variant: "destructive" });
      setBulkAssigning(false);
      return;
    }

    const { error } = await supabase
      .from("profiles")
      .update({ assigned_provider_id: providerId })
      .in("user_id", ids);

    if (error) {
      toast({ title: "Error", description: "Bulk assignment failed.", variant: "destructive" });
    } else {
      setPatients((prev) =>
        prev.map((p) => ids.includes(p.user_id) ? { ...p, assigned_provider_id: providerId } : p)
      );
      const provName = providerId
        ? providers.find((pr) => pr.user_id === providerId)
          ? `Dr. ${providers.find((pr) => pr.user_id === providerId)?.last_name || "Unknown"}`
          : "Provider"
        : "Unassigned";
      const skipMsg = skipped > 0 ? ` (${skipped} skipped — no assessment)` : "";
      toast({ title: "Bulk update complete", description: `${ids.length} patients assigned to ${provName}${skipMsg}` });
      setSelectedIds(new Set());
      setBulkProviderId(null);
    }
    setBulkAssigning(false);
  };

  const getProviderName = (providerId: string | null) => {
    if (!providerId) return null;
    const provider = providers.find((p) => p.user_id === providerId);
    return provider ? `Dr. ${provider.last_name || provider.first_name || "Unknown"}` : "Unknown";
  };

  const getPatientName = (p: Patient) => {
    if (p.first_name || p.last_name) {
      return `${p.first_name || ""} ${p.last_name || ""}`.trim();
    }
    return "Unnamed Patient";
  };

  const patientAccessor = (item: Patient, key: string): any => {
    if (key === "name") return getPatientName(item);
    if (key === "email") return item.email || "";
    if (key === "order_count") return item.order_count || 0;
    if (key === "last_sign_in_at") return item.last_sign_in_at || "";
    if (key === "last_checkup_date") return item.last_checkup_date || "";
    if (key === "created_at") return item.created_at;
    if (key === "assigned_provider") return getProviderName(item.assigned_provider_id) || "";
    return (item as any)[key];
  };

  const filteredPatients = sortData(patients.filter((p) => {
    const name = getPatientName(p).toLowerCase();
    const provider = getProviderName(p.assigned_provider_id)?.toLowerCase() || "";
    const email = (p.email || "").toLowerCase();
    const matchesSearch = name.includes(search.toLowerCase()) || provider.includes(search.toLowerCase()) || email.includes(search.toLowerCase());
    const matchesTag = !tagFilter || (p.tags && p.tags.includes(tagFilter));
    const matchesGlp1 = !glp1Only || (p.glp1Flags && p.glp1Flags.length > 0);
    const joinDate = new Date(p.created_at);
    const matchesDateFrom = !dateFrom || joinDate >= dateFrom;
    const matchesDateTo = !dateTo || joinDate <= new Date(dateTo.getTime() + 86400000 - 1);
    const patientAssessment = assessmentStatusMap[p.user_id] || null;
    const matchesAssessment = !assessmentFilter
      || (assessmentFilter === "none" && !patientAssessment)
      || (assessmentFilter !== "none" && patientAssessment === assessmentFilter);
    const matchesOverdue = !overdueOnly || (() => {
      const status = assessmentStatusMap[p.user_id];
      if (status !== "approved" && status !== "reviewed") return false;
      if (patientsWithAppointments.has(p.user_id)) return false;
      const assessData = assessmentDataMap[p.user_id];
      if (!assessData?.updated_at) return false;
      const daysSince = (Date.now() - new Date(assessData.updated_at).getTime()) / (1000 * 60 * 60 * 24);
      return daysSince >= 7;
    })();
    return matchesSearch && matchesTag && matchesGlp1 && matchesDateFrom && matchesDateTo && matchesAssessment && matchesOverdue;
  }), patientSort, patientAccessor);

  const totalPages = Math.max(1, Math.ceil(filteredPatients.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedPatients = filteredPatients.slice((safePage - 1) * pageSize, safePage * pageSize);

  // Reset to page 1 when search changes
  useEffect(() => { setCurrentPage(1); }, [search, tagFilter, glp1Only, dateFrom, dateTo, assessmentFilter, overdueOnly]);

  const exportCSV = () => {
    const headers = ["Name", "Email", "Phone", "Orders", "Active Orders", "Tags", "Assigned Provider", "Last Login", "Last Checkup", "Joined"];
    const rows = filteredPatients.map((p) => [
      getPatientName(p),
      p.email || "",
      p.phone || "",
      String(p.order_count || 0),
      String(p.active_order_count || 0),
      (p.tags || []).join("; "),
      getProviderName(p.assigned_provider_id) || "Unassigned",
      p.last_sign_in_at ? new Date(p.last_sign_in_at).toLocaleString() : "Never",
      p.last_checkup_date ? new Date(p.last_checkup_date).toLocaleDateString() : "",
      new Date(p.created_at).toLocaleDateString(),
    ]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${c.replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `patients-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast({ title: "Exported", description: `${filteredPatients.length} patients exported to CSV` });
  };

  const assignedCount = patients.filter((p) => p.assigned_provider_id).length;
  const unassignedCount = patients.length - assignedCount;
  const glp1Count = patients.filter((p) => p.glp1Flags && p.glp1Flags.length > 0).length;
  const overdueCount = patients.filter((p) => {
    const status = assessmentStatusMap[p.user_id];
    if (status !== "approved" && status !== "reviewed") return false;
    if (patientsWithAppointments.has(p.user_id)) return false;
    const assessData = assessmentDataMap[p.user_id];
    if (!assessData?.updated_at) return false;
    return (Date.now() - new Date(assessData.updated_at).getTime()) / (1000 * 60 * 60 * 24) >= 7;
  }).length;

  const noAssessmentCount = patients.filter((p) => !assessmentStatusMap[p.user_id]).length;
  const pendingAssessmentCount = patients.filter((p) => assessmentStatusMap[p.user_id] === "pending").length;
  const approvedAssessmentCount = patients.filter((p) => assessmentStatusMap[p.user_id] === "approved").length;
  const rejectedAssessmentCount = patients.filter((p) => assessmentStatusMap[p.user_id] === "rejected").length;

  if (loading) {
    return (
      <Card className="border-border bg-card">
        <CardContent className="p-12 text-center text-muted-foreground">
          Loading patients…
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { label: "Total Patients", value: patients.length, icon: Users, color: "text-foreground" },
          { label: "Assigned", value: assignedCount, icon: UserCheck, color: "text-primary" },
          { label: "Unassigned", value: unassignedCount, icon: Users, color: "text-yellow-600" },
          { label: "GLP-1 Flags", value: glp1Count, icon: ShieldAlert, color: "text-destructive" },
          { label: "Assessments", value: null, icon: FileText, color: "text-foreground",
            breakdown: [
              { label: "None", count: noAssessmentCount, dotClass: "bg-muted-foreground" },
              { label: "Pending", count: pendingAssessmentCount, dotClass: "bg-yellow-500" },
              { label: "Approved", count: approvedAssessmentCount, dotClass: "bg-green-600" },
              { label: "Rejected", count: rejectedAssessmentCount, dotClass: "bg-red-500" },
            ]
          },
        ].map((stat) => (
          <Card key={stat.label} className="border-border bg-card">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground font-medium">{stat.label}</p>
                  {stat.value !== null ? (
                    <p className={`text-2xl font-bold mt-1 ${stat.color}`}>{stat.value}</p>
                  ) : (
                    <div className="mt-1.5 space-y-0.5">
                      {(stat as any).breakdown.map((b: { label: string; count: number; dotClass: string }) => (
                        <div key={b.label} className="flex items-center gap-1.5 text-xs">
                          <span className={`inline-block h-2 w-2 rounded-full ${b.dotClass}`} />
                          <span className="text-muted-foreground">{b.label}</span>
                          <span className="font-semibold">{b.count}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <stat.icon size={20} className="text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Patient Table */}
      <Card className="border-border bg-card">
        <CardHeader className="pb-4">
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-3">
              <CardTitle className="text-lg font-display">Patient List</CardTitle>
              <div className="flex items-center gap-2 flex-wrap">
              <Button variant="outline" size="sm" className="h-9 text-xs gap-1.5" onClick={exportCSV}>
                <Download size={14} /> Export CSV
              </Button>
              {allTags.length > 0 && (
                <Select value={tagFilter || "__all__"} onValueChange={(v) => setTagFilter(v === "__all__" ? null : v)}>
                  <SelectTrigger className="w-44 h-9">
                    <div className="flex items-center gap-1.5 text-sm">
                      <Tag size={14} className="text-muted-foreground shrink-0" />
                      <SelectValue placeholder="Filter by tag" />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All Tags</SelectItem>
                    {allTags.map((tag) => (
                      <SelectItem key={tag} value={tag}>{tag}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {tagFilter && (
                <Button variant="ghost" size="sm" className="h-9 text-xs gap-1 text-muted-foreground" onClick={() => setTagFilter(null)}>
                  <X size={12} /> Clear
                </Button>
              )}
              <Select value={assessmentFilter || "__all__"} onValueChange={(v) => setAssessmentFilter(v === "__all__" ? null : v)}>
                <SelectTrigger className="w-44 h-9">
                  <div className="flex items-center gap-1.5 text-sm">
                    <FileText size={14} className="text-muted-foreground shrink-0" />
                    <SelectValue placeholder="Assessment" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All Assessments</SelectItem>
                  <SelectItem value="none">No Assessment</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="reviewed">Reviewed</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant={glp1Only ? "default" : "outline"}
                size="sm"
                className={cn("h-9 text-xs gap-1.5", glp1Only && "bg-destructive hover:bg-destructive/90 text-destructive-foreground")}
                onClick={() => setGlp1Only((v) => !v)}
              >
                <ShieldAlert size={14} />
                GLP-1 Flags
              </Button>
              <Button
                variant={overdueOnly ? "default" : "outline"}
                size="sm"
                className={cn("h-9 text-xs gap-1.5", overdueOnly && "bg-amber-600 hover:bg-amber-700 text-white")}
                onClick={() => setOverdueOnly((v) => !v)}
              >
                <Clock size={14} />
                Overdue ({overdueCount})
              </Button>
              <div className="relative w-64">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search patients or providers…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            </div>
            {/* Date range filter row */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Joined:</span>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className={cn("h-9 text-xs gap-1.5 w-36 justify-start font-normal", !dateFrom && "text-muted-foreground")}>
                    <CalendarIcon size={14} />
                    {dateFrom ? format(dateFrom, "MMM d, yyyy") : "From"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={dateFrom} onSelect={setDateFrom} initialFocus className={cn("p-3 pointer-events-auto")} />
                </PopoverContent>
              </Popover>
              <span className="text-xs text-muted-foreground">–</span>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className={cn("h-9 text-xs gap-1.5 w-36 justify-start font-normal", !dateTo && "text-muted-foreground")}>
                    <CalendarIcon size={14} />
                    {dateTo ? format(dateTo, "MMM d, yyyy") : "To"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={dateTo} onSelect={setDateTo} initialFocus className={cn("p-3 pointer-events-auto")} />
                </PopoverContent>
              </Popover>
              {(dateFrom || dateTo) && (
                <Button variant="ghost" size="sm" className="h-9 text-xs gap-1 text-muted-foreground" onClick={() => { setDateFrom(undefined); setDateTo(undefined); }}>
                  <X size={12} /> Clear dates
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        {/* Bulk action bar */}
        {selectedIds.size > 0 && (
          <div className="flex items-center gap-3 px-6 py-3 bg-primary/5 border-b border-border">
            <CheckSquare size={16} className="text-primary" />
            <span className="text-sm font-medium">{selectedIds.size} selected</span>
            <Select value={bulkProviderId || ""} onValueChange={setBulkProviderId}>
              <SelectTrigger className="w-48 h-9">
                <SelectValue placeholder="Assign provider…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE_VALUE}>
                  <span className="text-muted-foreground">Unassigned</span>
                </SelectItem>
                {providers.map((prov) => (
                  <SelectItem key={prov.user_id} value={prov.user_id}>
                    Dr. {prov.last_name || prov.first_name || "Unknown"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" disabled={!bulkProviderId || bulkAssigning} onClick={bulkAssignProvider}>
              {bulkAssigning ? "Assigning…" : "Apply"}
            </Button>
            <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={() => { setSelectedIds(new Set()); setBulkProviderId(null); }}>
              Clear selection
            </Button>
          </div>
        )}
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox
                    checked={paginatedPatients.length > 0 && paginatedPatients.every((p) => selectedIds.has(p.user_id))}
                    onCheckedChange={toggleSelectAll}
                  />
                </TableHead>
                <SortableTableHead label="Patient Name" sortKey="name" currentSort={patientSort} onSort={(k) => setPatientSort(nextSort(patientSort, k))} />
                <SortableTableHead label="Email" sortKey="email" currentSort={patientSort} onSort={(k) => setPatientSort(nextSort(patientSort, k))} />
                <TableHead>Flags</TableHead>
                <TableHead>Phone</TableHead>
                <SortableTableHead label="Orders" sortKey="order_count" currentSort={patientSort} onSort={(k) => setPatientSort(nextSort(patientSort, k))} />
                <SortableTableHead label="Last Login" sortKey="last_sign_in_at" currentSort={patientSort} onSort={(k) => setPatientSort(nextSort(patientSort, k))} />
                <SortableTableHead label="Last Checkup" sortKey="last_checkup_date" currentSort={patientSort} onSort={(k) => setPatientSort(nextSort(patientSort, k))} />
                <SortableTableHead label="Joined" sortKey="created_at" currentSort={patientSort} onSort={(k) => setPatientSort(nextSort(patientSort, k))} />
                <TableHead>Assessment</TableHead>
                <TableHead>Assigned Provider</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPatients.length === 0 ? (
                <TableRow>
                   <TableCell colSpan={11} className="text-center text-muted-foreground py-8">
                    No patients found.
                  </TableCell>
                </TableRow>
              ) : (
                paginatedPatients.map((p) => (
                  <TableRow key={p.user_id} className={cn(highlightPatientId === p.user_id && "bg-primary/10", selectedIds.has(p.user_id) && "bg-primary/5")}>
                    <TableCell>
                      <Checkbox
                        checked={selectedIds.has(p.user_id)}
                        onCheckedChange={() => toggleSelect(p.user_id)}
                      />
                    </TableCell>
                    <TableCell className="font-medium text-sm">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <button
                          onClick={() => setSelectedPatientId(p.user_id)}
                          className="text-primary hover:underline text-left"
                        >
                          {getPatientName(p)}
                        </button>
                        {p.tags && p.tags.map((tag) => (
                          <Badge key={tag} variant="secondary" className={`text-[10px] px-1.5 py-0 h-4 font-normal border-0 ${getTagColor(tag)}`}>
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{p.email || "—"}</TableCell>
                    <TableCell>
                      {(p.glp1Flags && p.glp1Flags.length > 0) ? (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              <Badge variant="destructive" className="text-xs gap-1 cursor-help">
                                <ShieldAlert size={12} /> GLP-1
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="max-w-xs">
                              <p className="text-xs font-semibold mb-1">GLP-1 Eligibility Concerns:</p>
                              <ul className="text-xs list-disc pl-3 space-y-0.5">
                                {p.glp1Flags.map(f => <li key={f}>{f}</li>)}
                              </ul>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{p.phone || "—"}</TableCell>
                    <TableCell>
                      {(p.order_count || 0) > 0 ? (
                        <button
                          onClick={() => onNavigateToOrders?.(p.user_id)}
                          className="inline-flex items-center gap-1.5 text-sm hover:underline"
                        >
                          <span className="text-muted-foreground">{p.order_count}</span>
                          {(p.active_order_count || 0) > 0 && (
                            <Badge variant="default" className="text-[10px] px-1.5 py-0 h-4">
                              {p.active_order_count} active
                            </Badge>
                          )}
                        </button>
                      ) : (
                        <span className="text-sm text-muted-foreground">0</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {p.last_sign_in_at
                        ? new Date(p.last_sign_in_at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })
                        : "Never"}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {p.last_checkup_date ? new Date(p.last_checkup_date).toLocaleDateString() : "—"}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {new Date(p.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const status = assessmentStatusMap[p.user_id];
                        if (!status) return <Badge variant="outline" className="text-xs text-muted-foreground">None</Badge>;
                        const statusStyles: Record<string, string> = {
                          pending: "bg-yellow-100 text-yellow-800",
                          reviewed: "bg-blue-100 text-blue-800",
                          approved: "bg-green-100 text-green-800",
                          rejected: "bg-red-100 text-red-800",
                        };
                        return (
                          <div className="flex items-center gap-1.5">
                            <Badge
                              className={`text-xs border-0 cursor-pointer hover:opacity-80 ${statusStyles[status] || ""}`}
                              onClick={(e) => { e.stopPropagation(); setViewingAssessmentPatientId(p.user_id); }}
                            >
                              <Eye className="h-3 w-3 mr-1" />
                              {status.charAt(0).toUpperCase() + status.slice(1)}
                            </Badge>
                            {status === "pending" && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-6 w-6 text-green-700 hover:text-green-900 hover:bg-green-100"
                                      onClick={(e) => { e.stopPropagation(); approveAssessment(p.user_id); }}
                                    >
                                      <CheckCircle2 className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Approve assessment</TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Popover open={rejectingPatientId === p.user_id} onOpenChange={(open) => { if (!open) { setRejectingPatientId(null); setRejectionReason(""); } }}>
                                      <PopoverTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="h-6 w-6 text-red-600 hover:text-red-800 hover:bg-red-100"
                                          onClick={(e) => { e.stopPropagation(); setRejectingPatientId(p.user_id); }}
                                        >
                                          <XCircle className="h-4 w-4" />
                                        </Button>
                                      </PopoverTrigger>
                                      <PopoverContent className="w-72 p-3" onClick={(e) => e.stopPropagation()}>
                                        <p className="text-sm font-medium mb-2">Rejection reason</p>
                                        <Input
                                          placeholder="e.g. Incomplete information"
                                          value={rejectionReason}
                                          onChange={(e) => setRejectionReason(e.target.value)}
                                          className="mb-2"
                                          autoFocus
                                        />
                                        <div className="flex justify-end gap-2">
                                          <Button variant="outline" size="sm" onClick={() => { setRejectingPatientId(null); setRejectionReason(""); }}>Cancel</Button>
                                          <Button variant="destructive" size="sm" onClick={rejectAssessment}>Reject</Button>
                                        </div>
                                      </PopoverContent>
                                    </Popover>
                                  </TooltipTrigger>
                                  <TooltipContent>Reject assessment</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </div>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      {assessmentStatusMap[p.user_id] && assessmentStatusMap[p.user_id] !== "pending" ? (
                        <Select
                          value={p.assigned_provider_id || NONE_VALUE}
                          onValueChange={(v) => assignProvider(p.user_id, v === NONE_VALUE ? null : v)}
                        >
                          <SelectTrigger className="w-48">
                            <SelectValue placeholder="Select provider" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={NONE_VALUE}>
                              <span className="text-muted-foreground">Unassigned</span>
                            </SelectItem>
                            {providers.map((prov) => (
                              <SelectItem key={prov.user_id} value={prov.user_id}>
                                Dr. {prov.last_name || prov.first_name || "Unknown"}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className="text-xs text-muted-foreground italic">
                          {assessmentStatusMap[p.user_id] === "pending" ? "Assessment pending" : "Assessment required"}
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-border">
            <p className="text-sm text-muted-foreground">
              Showing {(safePage - 1) * pageSize + 1}–{Math.min(safePage * pageSize, filteredPatients.length)} of {filteredPatients.length}
            </p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={safePage <= 1} onClick={() => setCurrentPage(safePage - 1)}>
                <ChevronLeft size={16} />
              </Button>
              <span className="text-sm font-medium">Page {safePage} of {totalPages}</span>
              <Button variant="outline" size="sm" disabled={safePage >= totalPages} onClick={() => setCurrentPage(safePage + 1)}>
                <ChevronRight size={16} />
              </Button>
            </div>
          </div>
        )}
      </Card>

      <PatientDetailDrawer
        patientId={selectedPatientId}
        onClose={() => setSelectedPatientId(null)}
      />

      {/* Assessment Quick View Dialog */}
      <Dialog open={!!viewingAssessmentPatientId} onOpenChange={(open) => { if (!open) setViewingAssessmentPatientId(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">Assessment Details</DialogTitle>
          </DialogHeader>
          {viewingAssessmentPatientId && assessmentDataMap[viewingAssessmentPatientId] && (() => {
            const a = assessmentDataMap[viewingAssessmentPatientId];
            const patient = patients.find((p) => p.user_id === viewingAssessmentPatientId);
            const statusStyles: Record<string, string> = {
              pending: "bg-yellow-100 text-yellow-800",
              reviewed: "bg-blue-100 text-blue-800",
              approved: "bg-green-100 text-green-800",
              rejected: "bg-red-100 text-red-800",
            };
            const fields = [
              { label: "Goal", value: a.goal },
              { label: "Peptide Interest", value: a.peptide_interest },
              { label: "Conditions", value: a.conditions },
              { label: "Allergies", value: a.allergies },
              { label: "Medications", value: a.medications },
              { label: "Additional Info", value: a.additional_info },
            ];
            return (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    {patient?.first_name || patient?.last_name
                      ? `${patient.first_name || ""} ${patient.last_name || ""}`.trim()
                      : patient?.email || "Unknown Patient"}
                  </p>
                  <Badge className={`text-xs border-0 ${statusStyles[a.status] || ""}`}>
                    {a.status.charAt(0).toUpperCase() + a.status.slice(1)}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground">Submitted {new Date(a.created_at).toLocaleDateString()}</p>
                <div className="grid gap-3">
                  {fields.map((f) => (
                    <div key={f.label}>
                      <p className="text-xs font-medium text-muted-foreground">{f.label}</p>
                      <p className="text-sm">{f.value || <span className="text-muted-foreground italic">Not provided</span>}</p>
                    </div>
                  ))}
                </div>
                {a.status === "pending" && (
                  <div className="flex justify-end gap-2 pt-2 border-t">
                    <Button variant="destructive" size="sm" onClick={() => { setViewingAssessmentPatientId(null); setRejectingPatientId(viewingAssessmentPatientId); }}>
                      <XCircle className="h-4 w-4 mr-1" /> Reject
                    </Button>
                    <Button size="sm" onClick={() => { approveAssessment(viewingAssessmentPatientId); setViewingAssessmentPatientId(null); }}>
                      <CheckCircle2 className="h-4 w-4 mr-1" /> Approve
                    </Button>
                  </div>
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PatientManagement;
